/* =========================================================================
   Dialog.tsx – Ultra Performance Version - No animations, maximum speed
   ======================================================================== */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { useGlobalLoading } from '@/store';

let globalDialogCounter = 0;

/* --------- 1. Variants -------------------------------------------------- */
const dialogVariants = cva(
  'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ' +
    'grid w-full gap-0 border shadow-lg ' +
    'bg-white border-slate-200 ' +
    'dark:bg-slate-950 dark:border-slate-700',
  {
    variants: {
      size: {
        xs: 'max-w-xs w-[90%] sm:w-full rounded-lg',
        sm: 'max-w-sm w-[90%] sm:w-full rounded-lg',
        md: 'max-w-md w-[90%] sm:w-full rounded-lg',
        lg: 'max-w-lg w-[90%] sm:w-full rounded-lg',
        xl: 'max-w-xl w-[90%] sm:w-full rounded-lg',
        '2xl': 'max-w-2xl w-[90%] sm:w-full rounded-lg',
        '3xl': 'max-w-3xl w-[90%] sm:w-full rounded-lg',
        '4xl': 'max-w-4xl w-[90%] sm:w-full rounded-lg',
        '5xl': 'max-w-5xl w-[95%] sm:w-full rounded-lg',
        full: 'h-screen max-w-full w-full rounded-none',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

/* --------- 2. Radix Shortcuts & Overlay ------------------------------- */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & { z: number }
>(({ className, z, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    style={{ zIndex: z }}
    className={cn('fixed inset-0 bg-black/40', className)}
    aria-hidden={undefined}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/* --------- 3. Content (z-index for nesting) ---------------------- */
interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | 'full';
  overlayClass?: string;
  overlayScroll?: boolean;
  hiddenCloseIcon?: boolean;
  contentPadding?: string;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      size,
      overlayClass,
      overlayScroll = false,
      hiddenCloseIcon = true,
      contentPadding = 'p-6',
      ...props
    },
    ref,
  ) => {
    // depth management - optimized for nested dialogs
    const [depth, setDepth] = React.useState<number>(0);

    React.useEffect(() => {
      globalDialogCounter += 1;
      setDepth(globalDialogCounter);

      return () => {
        globalDialogCounter -= 1;
      };
    }, []);

    // Ensure dialog overlays other surfaces like Sheet (which uses z-[999])
    const overlayZIndex = 5000 + depth * 20;
    const contentZIndex = overlayZIndex + 1;


    // Minimal DOM operations
    React.useEffect(() => {
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.removeAttribute('aria-hidden');
        document.body.removeAttribute('aria-hidden');
      }
    }, []);

    const { isLoading } = useGlobalLoading();

    return (
      <DialogPortal>
        <DialogOverlay
          z={overlayZIndex}
          className={cn(
            overlayScroll ? 'overflow-y-auto' : 'overflow-hidden',
            overlayClass,
          )}
        />

        <DialogPrimitive.Content
          ref={ref}
          style={{ zIndex: contentZIndex }}
          className={cn(
            dialogVariants({ size }),
            isLoading && 'pointer-events-none opacity-50',
            contentPadding,
            className,
          )}
          aria-modal="true"
          aria-hidden={undefined}
          aria-describedby={undefined}
          {...props}
        >
          {children}

          {!hiddenCloseIcon && (
            <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-full p-2 text-slate-500 transition-all hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100 dark:focus:ring-slate-600 dark:focus:ring-offset-slate-950">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

/* --------- 4. Header / Body / Footer / Title / Desc ------------------- */
function DialogHeader(p: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...p}
      className={cn(
        'flex flex-col space-y-3 border-b border-slate-200 px-6 py-6 ' +
          'dark:border-slate-700',
        p.className,
      )}
    />
  );
}

function DialogBody(p: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...p}
      className={cn(
        'max-h-[70vh] flex-1 overflow-y-auto px-6 py-6',
        p.className,
      )}
    />
  );
}

function DialogFooter(p: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...p}
      className={cn(
        'flex flex-col-reverse gap-3 border-t border-slate-200 ' +
          'px-6 py-6 dark:border-slate-700' +
          'sm:flex-row sm:justify-end sm:gap-2',
        p.className,
      )}
    />
  );
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>((props, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-xl font-semibold text-slate-900 dark:text-slate-100',
      props.className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>((props, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      'mt-1.5 text-sm text-slate-600 dark:text-slate-400',
      props.className,
    )}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

/* --------- 5. Exports -------------------------------------------------- */
export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
